import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AsyncSelect from 'react-select/async';
import FilterDrawer from '../../../shared/FilterDrawer';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import RequiredMark from '../../../shared/RequiredMark';
import RichTextField, { extractMentionedEntityIds } from '../../../shared/RichTextField';
import { useStaffAuth } from '../../../shared/StaffAuthContext';
import '../../../shared/StyledInput.css';
import {
  ACTIVITY_TYPE_LABELS, CHANNEL_ACTIVITY_TYPES, PICKABLE_CHANNELS, WEBSITE_CHANNEL, CHANNEL_LABELS,
  DIRECTIONS, DIRECTION_LABELS,
} from './activityConstants';
import { todayInTenant, formatDate } from '../../../shared/tenantClock';
import DateInput from '../../../shared/DateInput';

const fieldLabelStyle = { fontSize: 12, color: '#5a6b85', textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', marginBottom: 4 };
const fieldWrapStyle = { marginBottom: 14 };

// Matches styled-input's height, same styles object shape as
// AddLinkDrawer's selects (react-select can't inherit that chrome via
// className).
const contactSelectStyles = {
  container: (base) => ({ ...base, width: '100%' }),
  control: (base) => ({
    ...base,
    minHeight: 30,
    borderRadius: 4,
    borderColor: '#a9a9a9',
    boxShadow: 'none',
    fontSize: 14,
    '&:hover': { borderColor: '#a9a9a9' },
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  option: (base) => ({ ...base, fontSize: 14 }),
};

// Log-an-Activity modal (Call/Meeting/Email/Message/Task/Note, story #216) — also
// reused for Edit, opened by clicking an existing row on the Activities
// panel. Activity Type is fixed by whichever quick-action button opened
// this (or by the row being edited) — it isn't a field inside the form,
// since that's already how it gets chosen (see CommercialEntityDetailPage's
// quick-action row). Built on the same FilterDrawer shell AddLinkDrawer/
// AddTradeShowAttendanceDrawer already use for this page's other "add a
// related record" actions.
// followUpFrom (issue #218) is the Activity a new Task is being logged
// against as its "followed on from" back-link — set by the "Create
// follow-up Task" row action, null for a standalone Task (the quick-action
// icon) or any non-Task Activity Type. Only meaningful while creating; an
// existing Task's own back-link comes off editingActivity.followed_from_activity
// instead and, per the story's AC, is display-only here (created either
// standalone or as a follow-up — never re-linked afterwards).
// onFollowUp(saved), when given, adds "Save & Follow Up": save, then the
// caller swaps this drawer over to a new Task following up on `saved`.
function LogActivityDrawer({ open, onOpenChange, activityType, entity, contactOptions, editingActivity, followUpFrom, onSaved, onFollowUp }) {
  const { currentUser: activeUser } = useStaffAuth();
  const [channel, setChannel] = useState('');
  const [direction, setDirection] = useState('');
  const [outcome, setOutcome] = useState('');
  const [assignedUserId, setAssignedUserId] = useState('');
  const [userOptions, setUserOptions] = useState([]);
  // Every Activity Type is one-to-many (Core Concept 6, extended) — a
  // conference Call, a multi-recipient Email, a Meeting with several
  // attendees. {value, label} pairs (not just ids), since these come from
  // a live tenant-wide search (loadAttendeeOptions) rather than a
  // locally-held options list, so a label has to travel with its id to
  // redisplay a previously-picked contact.
  const [selectedAttendees, setSelectedAttendees] = useState([]);
  const [contactUnknown, setContactUnknown] = useState(false);
  const [topic, setTopic] = useState('');
  const [notes, setNotes] = useState('');
  const [activityDate, setActivityDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [outcomeOptions, setOutcomeOptions] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (editingActivity) {
      setChannel(editingActivity.channel || '');
      setDirection(editingActivity.direction || '');
      setOutcome(editingActivity.outcome || '');
      setSelectedAttendees((editingActivity.contacts || []).map((c) => ({ value: String(c.id), label: c.name, note: c.ActivityContact?.note || '' })));
      setContactUnknown(!!editingActivity.contact_unknown);
      setTopic(editingActivity.topic || '');
      setNotes(editingActivity.notes || '');
      setActivityDate(editingActivity.activity_date || '');
      setStartTime((editingActivity.start_time || '').slice(0, 5));
      setEndTime((editingActivity.end_time || '').slice(0, 5));
      setAssignedUserId(editingActivity.assigned_user_id ? String(editingActivity.assigned_user_id) : '');
    } else {
      setChannel('');
      setDirection('');
      setOutcome('');
      // Logging straight from an Individual's own page — they're obviously
      // who this was with, so pre-fill them rather than leaving the picker
      // blank (still removable, and more can be added alongside them).
      setSelectedAttendees(entity?.type === 'individual' ? [{ value: String(entity.id), label: entity.name }] : []);
      setContactUnknown(false);
      setTopic('');
      setNotes('');
      // Defaults to today, the day it's logged, so it sorts into the
      // Activity list where it belongs; change it for a Meeting or Call
      // scheduled ahead. A Task's date is its Due Date, and a Task can have
      // none, so it starts blank.
      setActivityDate(activityType === 'task' ? '' : todayInTenant());
      setStartTime('');
      setEndTime('');
      // A new Task defaults its assignee to whoever's logging it (Core
      // Concept 10) — still freely changeable via the select below.
      setAssignedUserId(activeUser?.id ? String(activeUser.id) : '');
    }
    setError('');
    // followUpFrom too: "Save & Follow Up" on a Task keeps the drawer open
    // and the type unchanged, so a new followUpFrom is what resets it.
  }, [open, editingActivity, entity, activeUser, activityType, followUpFrom]);

  const type = editingActivity ? editingActivity.activity_type : activityType;
  // type is null before any quick-action has ever been clicked (initial
  // drawerActivityType state) — this component still renders (just not
  // visibly, via FilterDrawer's own open-gated Portal) even while closed, so
  // every derived value below has to tolerate a null type.
  const typeLabel = ACTIVITY_TYPE_LABELS[type] || type || '';
  const showChannel = CHANNEL_ACTIVITY_TYPES.includes(type);
  // Task has no Channel/Direction/Outcome — it tracks completion a
  // different way, not this triad (Core Concept 9); its form shows an
  // Assignee + Due Date instead (Core Concept 10).
  const isTask = type === 'task';
  // Note has no Channel/Direction/Outcome either — it's a plain free-text
  // log, not a "contact" with a direction or an outcome to record. Doesn't
  // share Task's Assignee/Done-status handling, so kept as its own flag
  // rather than folded into isTask.
  const isNote = type === 'note';
  // Display-only — set once at creation (standalone vs. "Create follow-up
  // Task"), never itself re-editable through this form (see the AC and the
  // header comment above).
  const followedFromActivity = editingActivity ? editingActivity.followed_from_activity : followUpFrom;
  const contactSelectOptions = (contactOptions || []).map((c) => ({ value: String(c.id), label: c.name }));

  // Outcome is a tenant-editable lookup scoped per Activity Type (Settings
  // > CRM > Setup > Activity Outcomes) — fetched live rather than a local
  // constant. Type doesn't change while this drawer is open (it's fixed by
  // whichever quick-action button opened it), so one fetch per open covers
  // it.
  useEffect(() => {
    if (!open || !type || isTask || isNote) return;
    axios.get('/api/activity-outcomes', { params: { activity_type: type, status: 'active' } })
      .then((res) => setOutcomeOptions((res.data.data || []).map((o) => o.name)))
      .catch(() => setOutcomeOptions([]));
  }, [open, type, isTask]);

  // Assignee options — every active User in the tenant (Core Concept 10:
  // "assigned to myself or a teammate"). No dedicated picker component
  // exists elsewhere in the app for this yet, so this is a plain
  // styled-input select fed by /api/users?status=active, fetched locally,
  // matching how Outcome options are fetched locally here too rather than
  // threaded down as a prop.
  useEffect(() => {
    if (!open || !isTask) return;
    axios.get('/api/users', { params: { status: 'active' } })
      .then((res) => setUserOptions(res.data.data || []))
      .catch(() => setUserOptions([]));
  }, [open, isTask]);

  // Associated With searches across every Commercial Entity in the tenant —
  // People and Companies alike, not just this Entity's already-linked
  // People — since this covers both actual call/meeting attendees and a
  // purely contextual link to something unrelated to the contact itself
  // (e.g. a Lead worth flagging against an unrelated supplier call).
  // Excludes the entity itself (you're not your own attendee, and
  // associating an Activity with its own owner is meaningless).
  const loadAttendeeOptions = (inputValue, callback) => {
    if (!inputValue || inputValue.length < 1) return callback([]);
    axios.get('/api/commercial-entities/quick-search', {
      params: { q: inputValue, excludeId: entity?.id },
    })
      .then((res) => callback((res.data.data || []).map((r) => ({ value: String(r.id), label: r.name }))))
      .catch(() => callback([]));
  };

  const handleSave = async (followUp = false) => {
    if (isTask && !assignedUserId) return;
    setSaving(true);
    setError('');
    // @mentions typed into the note (see RichTextField.js) fold into the
    // same "Associated With" list as a manually-picked attendee — it's the
    // same association either way (models/activityContact.js) — so a
    // mentioned Person's own Activity feed picks this row up for free, "via"
    // labeled the same as any other purely-contextual link.
    const associationMap = new Map((contactUnknown ? [] : selectedAttendees).map((o) => [String(o.value), o.note || null]));
    if (!contactUnknown) {
      extractMentionedEntityIds(notes).forEach((id) => { if (!associationMap.has(id)) associationMap.set(id, null); });
    }
    const payload = {
      commercial_entity_id: entity.id,
      activity_type: type,
      channel: showChannel ? (channel || null) : null,
      direction: direction || null,
      outcome: outcome || null,
      associations: [...associationMap.entries()].map(([commercial_entity_id, note]) => ({ commercial_entity_id, note })),
      contact_unknown: contactUnknown,
      topic,
      notes,
      activity_date: activityDate || null,
      start_time: startTime || null,
      end_time: endTime || null,
      assigned_user_id: isTask ? (assignedUserId || null) : null,
      followed_from_activity_id: isTask ? (followedFromActivity?.id || null) : null,
    };
    try {
      const res = editingActivity
        ? await axios.put(`/api/activities/${editingActivity.id}`, payload)
        : await axios.post('/api/activities', payload);
      onSaved(res.data.data);
      if (followUp && onFollowUp) onFollowUp(res.data.data);
      else onOpenChange(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
    }
    setSaving(false);
  };

  return (
    <FilterDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={editingActivity ? `Edit ${typeLabel}` : `Log a ${typeLabel}`}
      description={editingActivity ? `Update this logged ${typeLabel.toLowerCase()}.` : `Log a ${typeLabel.toLowerCase()} against ${entity?.name || 'this record'}.`}
      footer={(
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <CancelButton onClick={() => onOpenChange(false)} disabled={saving} />
          {onFollowUp && (
            <SaveButton onClick={() => handleSave(true)} loading={saving} disabled={isTask && !assignedUserId} type="button" title="Save, then log a follow-up Task">
              {saving ? 'Saving...' : 'Save & Follow Up'}
            </SaveButton>
          )}
          <SaveButton onClick={() => handleSave()} loading={saving} disabled={isTask && !assignedUserId} type="button" />
        </div>
      )}
    >
      <div style={fieldWrapStyle}>
        <label style={fieldLabelStyle}>Topic</label>
        <input
          className="styled-input"
          type="text"
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="e.g. Renewal pricing discussion"
          maxLength={150}
          style={{ width: '100%', fontSize: 14 }}
        />
      </div>

      {showChannel && (
        <div style={fieldWrapStyle}>
          <label style={fieldLabelStyle}>Channel</label>
          <select className="styled-input" value={channel} onChange={(e) => setChannel(e.target.value)} style={{ width: '100%', fontSize: 14 }}>
            <option value="">(none)</option>
            {(editingActivity?.channel === WEBSITE_CHANNEL ? [...PICKABLE_CHANNELS, WEBSITE_CHANNEL] : PICKABLE_CHANNELS)
              .map((c) => <option key={c} value={c}>{CHANNEL_LABELS[c]}</option>)}
          </select>
        </div>
      )}

      {!isTask && !isNote && (
        <div style={fieldWrapStyle}>
          <label style={fieldLabelStyle}>Direction</label>
          <select className="styled-input" value={direction} onChange={(e) => setDirection(e.target.value)} style={{ width: '100%', fontSize: 14 }}>
            <option value="">(none)</option>
            {DIRECTIONS.map((d) => <option key={d} value={d}>{DIRECTION_LABELS[d]}</option>)}
          </select>
        </div>
      )}

      {!isTask && !isNote && (
        <div style={fieldWrapStyle}>
          <label style={fieldLabelStyle}>Outcome</label>
          <select className="styled-input" value={outcome} onChange={(e) => setOutcome(e.target.value)} style={{ width: '100%', fontSize: 14 }}>
            <option value="">(none)</option>
            {outcome && !outcomeOptions.includes(outcome) && <option value={outcome}>{outcome} (deactivated)</option>}
            {outcomeOptions.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        </div>
      )}

      {isTask && followedFromActivity && (
        <div style={{ ...fieldWrapStyle, fontSize: 13, color: '#5a6b85', background: '#f5f7fa', border: '1px solid #eef0f3', borderRadius: 4, padding: '8px 10px' }}>
          Following up on <strong>{ACTIVITY_TYPE_LABELS[followedFromActivity.activity_type] || followedFromActivity.activity_type}</strong>
          {followedFromActivity.topic ? ` — ${followedFromActivity.topic}` : ''}
          {followedFromActivity.activity_date ? ` (${formatDate(followedFromActivity.activity_date)})` : ''}
        </div>
      )}

      {isTask && (
        <div style={fieldWrapStyle}>
          <label style={fieldLabelStyle}>Assignee<RequiredMark /></label>
          <select className="styled-input" value={assignedUserId} onChange={(e) => setAssignedUserId(e.target.value)} style={{ width: '100%', fontSize: 14 }}>
            <option value="">(none)</option>
            {assignedUserId && !userOptions.some((u) => String(u.id) === assignedUserId) && (
              <option value={assignedUserId}>{editingActivity?.assignee?.name || 'Deactivated user'}</option>
            )}
            {userOptions.map((u) => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
          </select>
        </div>
      )}

      <div style={fieldWrapStyle}>
        <label style={fieldLabelStyle}>Associated With</label>
        <AsyncSelect
          isMulti
          isDisabled={contactUnknown}
          cacheOptions
          defaultOptions={contactSelectOptions}
          loadOptions={loadAttendeeOptions}
          value={selectedAttendees}
          onChange={(selected) => setSelectedAttendees((selected || []).map((o) => ({ ...o, note: o.note || '' })))}
          placeholder="Search people or companies..."
          styles={contactSelectStyles}
          noOptionsMessage={() => 'No matches found'}
          loadingMessage={() => 'Searching...'}
        />
        {/* Optional per-Entity "why is this linked" — mainly useful for an
            Entity added purely for context (e.g. a Lead flagged against an
            unrelated supplier call) rather than an actual attendee, but
            shown for every selected Entity since there's no separate
            "attendee vs. context link" distinction in the data (see
            models/activityContact.js) — only whether the linked Entity is
            also this Activity's own owner. */}
        {!contactUnknown && selectedAttendees.length > 0 && (
          <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {selectedAttendees.map((a, i) => (
              <div key={a.value} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span style={{ fontSize: 12, color: '#5a6b85', maxWidth: 130, flexShrink: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={a.label}>{a.label}</span>
                <input
                  className="styled-input"
                  type="text"
                  value={a.note || ''}
                  onChange={(e) => {
                    const note = e.target.value;
                    setSelectedAttendees((prev) => prev.map((x, idx) => (idx === i ? { ...x, note } : x)));
                  }}
                  placeholder="Why associated (optional)"
                  style={{ flex: 1, fontSize: 12, height: 26 }}
                />
              </div>
            ))}
          </div>
        )}
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', marginTop: 6 }}>
          <input
            type="checkbox"
            checked={contactUnknown}
            onChange={(e) => { setContactUnknown(e.target.checked); if (e.target.checked) setSelectedAttendees([]); }}
          />
          Contact Unknown
        </label>
      </div>

      <div style={fieldWrapStyle}>
        <label style={fieldLabelStyle}>Notes</label>
        <RichTextField value={notes} onChange={setNotes} placeholder="Add a note..." />
      </div>

      <div style={{ ...fieldWrapStyle, display: 'flex', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={fieldLabelStyle}>{isTask ? 'Due Date' : 'Activity Date'}</label>
          <DateInput
            className="styled-input"
            value={activityDate}
            onChange={(e) => setActivityDate(e.target.value)}
            style={{ fontSize: 14, width: '100%' }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={fieldLabelStyle}>Start Time</label>
          <input
            type="time"
            className="styled-input"
            value={startTime}
            onChange={(e) => setStartTime(e.target.value)}
            style={{ fontSize: 14, width: '100%' }}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label style={fieldLabelStyle}>End Time</label>
          <input
            type="time"
            className="styled-input"
            value={endTime}
            onChange={(e) => setEndTime(e.target.value)}
            style={{ fontSize: 14, width: '100%' }}
          />
        </div>
      </div>

      {error && <div style={{ color: 'red', fontSize: 13, marginTop: 8 }}>{error}</div>}
    </FilterDrawer>
  );
}

export default LogActivityDrawer;
